<template>
  <div class="chat-container" :class="{ 'loading': loading }">
    <b-loading :model-value="loading" :is-full-page="false"></b-loading>

    <div v-if="!loading && messages.length > 0">
      <div v-for="(message, index) in messages" :key="index" class="message-wrapper" :class="message.isAimee ? 'aimee-message' : 'user-message'">
        <div class="avatar-container" v-if="message.isAimee">
          <div v-if="shouldShowAvatar(index)" class="avatar aimee-avatar">
            <img src="@/assets/aimee.png" alt="Aimee" class="aimee-icon">
          </div>
          <div v-else class="avatar-spacer"></div>
        </div>
        <div class="message-bubble" :class="message.isAimee ? 'is-aimee' : 'is-user'">
          <div class="message-content">
            <!-- Tool call badges for historical messages -->
            <div v-if="message.toolCalls && message.toolCalls.length > 0" class="tool-calls-badges">
              <span v-for="tc in message.toolCalls" :key="tc.id" class="tool-call-badge">
                <i class="mdi mdi-function"></i>
                {{ formatToolName(tc.name) }}
              </span>
            </div>
            <!-- Active tool call indicator during streaming -->
            <div v-if="message.isAimee && !message.text && activeToolCall" class="tool-call-indicator">
              <i class="mdi mdi-cog-sync mdi-spin"></i>
              {{ formatToolName(activeToolCall) }}...
            </div>
            <div v-else-if="message.isAimee && !message.text" class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <vue-markdown v-else-if="message.isAimee" :markdown="message.text" :remark-plugins="remarkPlugins" />
            <span v-else>{{ message.text }}</span>
          </div>
        </div>
        <div class="avatar-container" v-if="!message.isAimee">
          <div v-if="shouldShowAvatar(index)" class="avatar user-avatar">
            <user-avatar v-if="participant"
                         :user="participant"
                         :size="32" />
            <i v-else class="mdi mdi-account"></i>
          </div>
          <div v-else class="avatar-spacer"></div>
        </div>
      </div>
    </div>

    <div v-else-if="!loading && emptyMessage" class="has-text-centered has-text-grey py-5">
      <p>{{ emptyMessage }}</p>
    </div>
  </div>
</template>

<script>
import UserAvatar from "@/components/UserAvatar.vue"
import { VueMarkdown } from '@crazydos/vue-markdown'
import remarkGfm from 'remark-gfm'

export default {
  name: "AimeeChatMessages",
  components: { UserAvatar, VueMarkdown },
  data() {
    return {
      remarkPlugins: [remarkGfm]
    }
  },
  props: {
    messages: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    participant: {
      type: Object,
      default: null
    },
    emptyMessage: {
      type: String,
      default: "No messages found in this conversation"
    },
    activeToolCall: {
      type: String,
      default: null
    }
  },
  methods: {
    shouldShowAvatar(index) {
      if (index === this.messages.length - 1) return true
      if (this.messages[index].isAimee !== this.messages[index + 1].isAimee) return true
      return false
    },
    formatToolName(name) {
      return "Calling " + name + " Tool"
      // const toolLabels = {
      //   'patient_list_tool': 'Fetching patients',
      //   'patient_event_tool': 'Loading events',
      //   'patient_snapshot_tool': 'Analyzing snapshots',
      //   'user_list_tool': 'Fetching users',
      //   'video_device_tool': 'Loading devices'
      // }
      // return toolLabels[name] || name?.replace(/_/g, ' ').replace(/tool$/i, '').trim() || 'Working'
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-container {
  max-height: 500px;
  overflow-y: auto;
  padding: 16px 10px;
  margin: 0 -20px;
  position: relative;

  :deep(.loading-overlay) {
    left: -10px;
    right: -10px;
    bottom: 0;
    top: 0;
  }

  &.loading {
    padding: 40px;
  }
}

.message-wrapper {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-end;

  &.aimee-message {
    justify-content: flex-start;
  }

  &.user-message {
    justify-content: flex-end;
  }
}

.message-bubble {
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 16px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  &.is-aimee {
    background-color: #e9ecef;
    color: #000;
  }

  &.is-user {
    background-color: #007bff;
    color: white;
  }
}

.message-content {
  font-size: 14px;
  line-height: 1.4;
  white-space: normal;
  :deep(> div > *:not(:last-child)) {
    margin-bottom: 15px;
  }

  :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
    //margin: 8px 0;
    font-weight: 500;
  }

  :deep(h1) { font-size: 16px; }
  :deep(h2) { font-size: 15px; }
  :deep(h3) { font-size: 14px; }
  :deep(h4) { font-size: 14px; }
  :deep(h5) { font-size: 14px; }
  :deep(h6) { font-size: 14px; }

  :deep(ul), :deep(ol) {
    margin: 8px 15px;
    list-style-type: disc;
  }

  :deep(li) {
    margin-bottom: 10px;
  }

  :deep(code) {
    background-color: rgba(0, 0, 0, 0.08);
    //padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
  }

  :deep(pre) {
    background-color: rgba(0, 0, 0, 0.08);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    //margin: 8px 0;

    code {
      background-color: transparent;
      padding: 0;
    }
  }

  :deep(blockquote) {
    border-left: 3px solid rgba(0, 0, 0, 0.2);
    padding-left: 12px;
    //margin: 8px 0;
    font-style: italic;
  }

  :deep(a) {
    color: #007bff;
    text-decoration: underline;
  }

  :deep(strong) {
    font-weight: 500;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(hr) {
    //border: none;
    //border-top: 1px solid rgba(0, 0, 0, 0.1);
    //margin: 12px 0;
    margin: 30px 0;
  }

  :deep(table) {
    border-collapse: collapse;
    width: 100%;
    //margin: 8px 0;
  }

  :deep(th), :deep(td) {
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 6px 8px;
    text-align: left;
  }

  :deep(th) {
    background-color: rgba(0, 0, 0, 0.05);
    font-weight: 600;
  }
}

.avatar-container {
  margin: 0 8px;
}

.avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.aimee-avatar {
  background: linear-gradient(0deg, rgba(164, 56, 168, 1) 0%, rgba(104, 91, 199, 1) 100%);
  color: white;
}

.user-avatar {
  background-color: #007bff;
  color: white;
}

.avatar-spacer {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border: 2px solid transparent;
}

.aimee-icon {
  width: 22px;
  height: 22px;
}

.loading-dots {
  display: flex;
  gap: 4px;
  padding: 6px 0;
  margin-bottom: -16px;

  span {
    width: 8px;
    height: 8px;
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    animation: loading-bounce 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.tool-call-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 13px;
  padding: 4px 0;

  .mdi {
    font-size: 16px;
    color: #685bc7;
  }
}

.tool-calls-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.tool-call-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: rgba(104, 91, 199, 0.1);
  color: #685bc7;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;

  .mdi {
    font-size: 12px;
  }
}
</style>
